import { useMemo, useState } from "react";
import { hotTopics, discussTopics, viralTopics, type HotTopic } from "@/mocks/hotlist";

interface TabConfig {
  key: string;
  label: string;
  desc: string;
  metric: string;
  data: HotTopic[];
}

const TABS: TabConfig[] = [
  {
    key: "hot",
    label: "热度榜",
    desc: "按综合关注量排序，看哪个话题被最多人看到。",
    metric: "热度",
    data: hotTopics,
  },
  {
    key: "discuss",
    label: "热议榜",
    desc: "按讨论量排序，观点交锋最激烈的话题都在这。",
    metric: "讨论量",
    data: discussTopics,
  },
  {
    key: "viral",
    label: "爆款排行",
    desc: "按播放与转发增长排序，发现正在快速传播的内容。",
    metric: "传播量",
    data: viralTopics,
  },
];

export default function RankingSwitcher() {
  const [activeKey, setActiveKey] = useState("discuss");
  const [query, setQuery] = useState("");

  const activeTab = TABS.find((tab) => tab.key === activeKey) ?? TABS[0];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeTab.data;
    return activeTab.data.filter((item) => {
      const haystack = `${item.title}${item.category}${item.tags.join("")}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [activeTab, query]);

  return (
    <section id="ranking" className="w-full py-16 md:py-24 bg-background-100 border-y border-background-200/70">
      <div className="w-full px-4 md:px-8">
        <div className="w-full max-w-6xl mx-auto">
          <div className="mb-8 md:mb-10">
            <span className="inline-flex items-center gap-2 text-xs md:text-sm text-accent-400 font-medium">
              <span className="w-6 h-px bg-accent-500" />
              榜单专区
            </span>
            <h2 className="mt-3 font-heading text-3xl md:text-5xl font-bold text-foreground-950">
              一份榜单，三种看法
            </h2>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
              热度、热议、爆款来自三套不同的统计口径。想看热闹就切热度榜，想找观点就切热议榜，想追正在起量的内容就切爆款排行。
            </p>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-200/70 w-full sm:w-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setActiveKey(tab.key);
                    setQuery("");
                  }}
                  className={`flex-1 sm:flex-none px-4 md:px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    activeKey === tab.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="relative w-full lg:w-72">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索话题、分类或标签"
                className="w-full pl-10 pr-3 py-2.5 rounded-md bg-background-50 border border-background-300/70 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/70 transition-colors"
              />
            </div>
          </div>

          <p className="mb-6 text-xs md:text-sm text-foreground-600">{activeTab.desc}</p>

          {filtered.length === 0 ? (
            <div className="rounded-lg border border-background-200/70 bg-background-50 py-16 text-center">
              <span className="inline-flex w-12 h-12 rounded-full bg-background-200/70 items-center justify-center mb-4">
                <i className="ri-search-eye-line text-xl text-foreground-500" />
              </span>
              <p className="text-sm text-foreground-600">
                没有匹配「{query}」的话题，换个关键词试试。
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filtered.map((item) => (
                <article
                  key={`${activeKey}-${item.rank}`}
                  className="rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5 flex gap-4 hover:border-primary-500/40 transition-colors"
                >
                  <span
                    className={`shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center font-heading text-lg md:text-xl font-bold tabular-nums ${
                      item.rank <= 3
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-200/70 text-foreground-700"
                    }`}
                  >
                    {item.rank}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug">
                        {item.title}
                      </h3>
                    </div>
                    <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed">
                      {item.summary}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="px-2 py-0.5 rounded-md bg-secondary-500/10 text-xs text-secondary-300 whitespace-nowrap">
                        {item.category}
                      </span>
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-md bg-background-200/70 text-xs text-foreground-600 whitespace-nowrap"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-background-200/60 flex items-center gap-4 text-xs md:text-sm">
                      <span className="text-primary-400 font-medium tabular-nums">
                        {activeTab.metric} {item.heat}
                      </span>
                      <span className="text-foreground-600">讨论 {item.discussion}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}